import React from 'react';
import { cn } from '@/lib/utils';
import { 
  Facebook, 
  LinkedIn, 
  YouTube, 
  Instagram,
  TikTok,
  Phone, 
  Mail, 
  MapPin 
} from '@/components/ui/icons';
import { useResponsive, getResponsiveTextClasses, getResponsiveSpacingClasses, getFocusClasses, getTouchAccessibilityClasses } from '@/lib/responsive-utils';
import { footerData, footerLocations } from '@/data/footer';

interface FooterProps {
  className?: string;
}

// Icon mapping for social media
const socialIcons = {
  Facebook,
  LinkedIn,
  YouTube,
  Instagram,
  TikTok,
} as const;

export const Footer: React.FC<FooterProps> = ({ className }) => {
  const { isMobile } = useResponsive();
  const handleLinkClick = (href: string, isExternal?: boolean) => {
    if (isExternal) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else if (href.startsWith('#')) {
      // Smooth scroll to section
      const targetId = href.substring(1);
      const targetElement = document.getElementById(targetId);
      
      if (targetElement) {
        const headerHeight = 80; // Account for fixed header height
        const targetPosition = targetElement.offsetTop - headerHeight;
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      window.location.href = href;
    }
  };

  return (
    <footer className={cn('bg-liec-neutral-900 text-white', getTouchAccessibilityClasses(), className)} role="contentinfo">
      {/* Main Footer Content */}
      <div className={`w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${getResponsiveSpacingClasses('py', 12, 14, 16)}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-8 lg:gap-12">
          {/* Company Info - Takes 2 columns on xl screens */}
          <div className="lg:col-span-2 xl:col-span-2">
            {/* Logo and Company Name */}
            <div className={`flex items-center space-x-3 ${getResponsiveSpacingClasses('mb', 4, 5, 6)}`}>
              <div className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'}  rounded-lg flex items-center justify-center`}>
               <img 
                src="/LIEC_Logo_Blanco_Principal_V1.svg" 
                alt="LIEC - Laboratorio de Control de Calidad de Materiales" 
                className="h-10 sm:h-12 lg:h-14 w-auto object-contain transition-all duration-300 ease-in-out group-hover:scale-105"
              />
              </div>
              <div>
                <div className={`text-white font-bold ${getResponsiveTextClasses('text-lg', 'text-xl', 'text-xl')} leading-tight`}>
                  LIEC
                </div>
                <div className={`text-liec-neutral-300 ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')} leading-tight`}>
                  Control de Calidad de Materiales
                </div>
              </div>
            </div>

            {/* Company Description */}
            <p className={`text-liec-neutral-300 ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')} leading-relaxed ${getResponsiveSpacingClasses('mb', 4, 5, 6)}`}>
              {footerData.description}
            </p>

            {/* Contact Information */}
            <div className="space-y-2 sm:space-y-3">
              {/* Phones */}
              {footerData.contactInfo.phones.map((phone, index) => (
                <div key={`phone-${index}`} className={`flex items-center space-x-3 ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')}`}>
                  <Phone size={isMobile ? 14 : 16} className="text-liec-primary-300 flex-shrink-0" />
                  <a 
                    href={`tel:${phone.replace(/\s+/g, '').replace(/[()]/g, '')}`}
                    className={`text-liec-neutral-300 hover:text-liec-primary-300 transition-colors duration-200 ${getFocusClasses()}`}
                  >
                    {phone}
                  </a>
                </div>
              ))}
              
              {/* WhatsApp */}
              {footerData.contactInfo.whatsapp && (
                <div className={`flex items-center space-x-3 ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')}`}>
                  <svg 
                    width={isMobile ? 14 : 16} 
                    height={isMobile ? 14 : 16} 
                    viewBox="0 0 24 24" 
                    fill="currentColor" 
                    className="text-liec-primary-300 flex-shrink-0"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  <a 
                    href={`https://wa.me/${footerData.contactInfo.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-liec-neutral-300 hover:text-liec-primary-300 transition-colors duration-200 ${getFocusClasses()}`}
                  >
                    {footerData.contactInfo.whatsapp}
                  </a>
                </div>
              )}
              
              {/* Emails */}
              {footerData.contactInfo.emails.map((email, index) => (
                <div key={`email-${index}`} className={`flex items-center space-x-3 ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')}`}>
                  <Mail size={isMobile ? 14 : 16} className="text-liec-primary-300 flex-shrink-0" />
                  <a 
                    href={`mailto:${email}`}
                    className={`text-liec-neutral-300 hover:text-liec-primary-300 transition-colors duration-200 ${getFocusClasses()}`}
                  >
                    {email}
                  </a>
                </div>
              ))}
              
              {/* Address */}
              <div className={`flex items-start space-x-3 ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')}`}>
                <MapPin size={isMobile ? 14 : 16} className="text-liec-primary-300 flex-shrink-0 mt-0.5" />
                <span className="text-liec-neutral-300 leading-relaxed">
                  {footerData.contactInfo.address}
                </span>
              </div>
            </div>

            
          </div>


          {/* Office Locations - Takes 2 columns on xl screens */}
          <div className="lg:col-span-4 xl:col-span-4 ml-9">
            <h3 className={`text-white font-semibold ${getResponsiveTextClasses('text-base', 'text-lg', 'text-lg')} ${getResponsiveSpacingClasses('mb', 3, 4, 4)}`}>
              Nuestras Sucursales
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {footerLocations.map((location) => (
                <div key={location.name} className="border-l-2 border-liec-primary-400 pl-4">
                  <h4 className={`text-white font-medium ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')} mb-1`}>
                    {location.name}
                  </h4>
                  <p className={`text-liec-neutral-300 ${getResponsiveTextClasses('text-xs', 'text-xs', 'text-xs')} mb-1`}>
                    {location.city}
                  </p>
                  {/* Multiple phones */}
                  {location.phones.map((phone, index) => (
                    <div key={`${location.name}-phone-${index}`} className="flex items-center space-x-2 mb-0.5">
                      <Phone size={isMobile ? 10 : 12} className="text-liec-primary-300" />
                      <a 
                        href={`tel:${phone.replace(/\s+/g, '').replace(/[()]/g, '')}`}
                        className={`text-liec-neutral-300 hover:text-liec-primary-300 transition-colors duration-200 ${getResponsiveTextClasses('text-xs', 'text-xs', 'text-xs')}`}
                      >
                        {phone}
                      </a>
                    </div>
                  ))}
                  {/* WhatsApp */}
                  {location.whatsapp && (
                    <div className="flex items-center space-x-2 mb-0.5">
                      <svg 
                        width={isMobile ? 12 : 14} 
                        height={isMobile ? 12 : 14} 
                        viewBox="0 0 24 24" 
                        fill="currentColor" 
                        className="text-liec-primary-300 flex-shrink-0"
                      >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      <a 
                        href={`https://wa.me/${location.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-liec-neutral-300 hover:text-liec-primary-300 transition-colors duration-200 ${getResponsiveTextClasses('text-xs', 'text-xs', 'text-xs')}`}
                      >
                        {location.whatsapp}
                      </a>
                    </div>
                  )}
                  {/* Multiple emails */}
                  {location.emails && location.emails.length > 0 && location.emails.map((email, index) => (
                    <div key={`${location.name}-email-${index}`} className="flex items-center space-x-2 mb-0.5">
                      <Mail size={isMobile ? 10 : 12} className="text-liec-primary-300" />
                      <a 
                        href={`mailto:${email}`}
                        className={`text-liec-neutral-300 hover:text-liec-primary-300 transition-colors duration-200 ${getResponsiveTextClasses('text-xs', 'text-xs', 'text-xs')}`}
                      >
                        {email}
                      </a>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Social Media Links */}
            <div className= { `  ${getResponsiveSpacingClasses('mt', 4, 5, 6)} ` }>
              <h4 className={`text-white flex justify-center  font-semibold ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')} ${getResponsiveSpacingClasses('mb', 2, 3, 3)}`}>Síguenos</h4>
              <div className="flex justify-center  space-x-4">
                {footerData.socialMedia.map((social) => {
                  const IconComponent = socialIcons[social.icon as keyof typeof socialIcons];
                  return (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${isMobile ? ' w-8 h-8' : 'w-10 h-10'} 
                      
                      rounded flex items-center justify-center text-liec-neutral-300 hover:text-liec-primary-300  transition-all duration-200 group ${getFocusClasses()}`}
                      aria-label={`Síguenos en ${social.name}`}
                    >
                      <IconComponent size={isMobile ? 16 : 20} />
                    </a>
                  );
                })}
              </div>
            </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="border-t border-liec-neutral-800">
        <div className={`w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${getResponsiveSpacingClasses('py', 4, 5, 6)}`}>
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className={`text-liec-neutral-400 ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')} text-center md:text-left`}>
              {footerData.copyright}
            </div>
            <div className={`flex flex-wrap justify-center md:justify-end space-x-6 ${getResponsiveTextClasses('text-xs', 'text-sm', 'text-sm')}`}>
              <button
                onClick={() => handleLinkClick('#terminos', true)}
                className={`text-liec-neutral-400 hover:text-liec-primary-400 transition-colors duration-200 ${getFocusClasses()}`}
              >
                Términos y Condiciones
              </button>
              <button
                onClick={() => handleLinkClick('#privacidad', true)}
                className={`text-liec-neutral-400 hover:text-liec-primary-400 transition-colors duration-200 ${getFocusClasses()}`}
              >
                Política de Privacidad
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;